import React from "react";

import Icon from "components/AppIcon";
import Image from "components/AppImage";

const AI_TOOLS = [
{
  id: "game",
  icon: "Gamepad2",
  title: "AI Game Generator",
  subtitle: "Text to Game",
  description: "Describe any game concept and AI builds a fully playable browser game with mechanics, art, and logic in seconds.",
  color: "#6366F1",
  gradient: "linear-gradient(135deg, #6366F1, #8B5CF6)",
  mode: "text-to-game",
  examples: [
  { title: "Neon Dungeon Crawler", plays: "12.4K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_171eb3e22-1772147609983.png", alt: "Neon cyberpunk dungeon game with glowing corridors" },
  { title: "Crystal Collector", plays: "9.8K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_12dfead97-1773039606671.png", alt: "Colorful platformer with glowing crystals and robot" },
  { title: "Pixel Racer Turbo", plays: "15.6K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_1a68a7a34-1773039609618.png", alt: "Retro pixel art racing game with neon speed lines" }],

  tags: ["Platformer", "Puzzle", "RPG", "Arcade", "Shooter"]
},
{
  id: "video",
  icon: "Video",
  title: "AI Video Generator",
  subtitle: "Script to Video",
  description: "Transform scripts and storylines into cinematic video trailers with AI-generated visuals and professional editing.",
  color: "#10B981",
  gradient: "linear-gradient(135deg, #10B981, #06B6D4)",
  mode: "script-to-video",
  examples: [
  { title: "Space Odyssey Intro", plays: "5.4K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_1a878e644-1773074696693.png", alt: "Futuristic spaceship in deep space" },
  { title: "Ocean Adventure", plays: "2.1K", img: "https://images.unsplash.com/photo-1691859653245-e88f19240355", alt: "Crystal clear ocean with coral reef and tropical fish" },
  { title: "Dragon Quest Intro", plays: "3.2K", img: "https://images.unsplash.com/photo-1594209134844-1614f446a5e0", alt: "Medieval castle silhouette against fiery sunset" }],

  tags: ["Trailer", "Cinematic", "Short Film", "Promo"]
},
{
  id: "animation",
  icon: "Film",
  title: "AI Animation Studio",
  subtitle: "Text to Animation",
  description: "Create smooth character animations, cutscenes, and motion graphics from simple text descriptions.",
  color: "#F59E0B",
  gradient: "linear-gradient(135deg, #F59E0B, #EF4444)",
  mode: "text-to-animation",
  examples: [
  { title: "Dragon Flight", plays: "8.7K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_13f179f9f-1773039607602.png", alt: "Majestic dragon soaring through dramatic sunset clouds" },
  { title: "Forest Guardian", plays: "14.2K", img: "https://images.unsplash.com/photo-1731685131186-191e77db24e5", alt: "Lush green forest with ancient trees and dappled sunlight" },
  { title: "Cosmic Shooter", plays: "7.3K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_14fcb1ba9-1772243734629.png", alt: "Colorful space shooter with vibrant nebula background" }],

  tags: ["Character", "Cutscene", "Logo", "Motion"]
},
{
  id: "music",
  icon: "Music",
  title: "AI Music Generator",
  subtitle: "Text to Music",
  description: "Generate original copyright-safe music in any style — cinematic, afrobeat, gospel, lo-fi, electronic, and more.",
  color: "#8B5CF6",
  gradient: "linear-gradient(135deg, #8B5CF6, #EC4899)",
  mode: "text-to-music",
  examples: [
  { title: "Neon City Beats", plays: "21K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c991270d-1773039609949.png", alt: "Vibrant neon city with pixel art characters dancing" },
  { title: "Epic Orchestral", plays: "6.2K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_11e3aeabc-1773074689943.png", alt: "Dramatic orchestral music visualization with waves" },
  { title: "Afrobeat Groove", plays: "9.1K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_10f572d7e-1773074696218.png", alt: "Colorful music visualization with African rhythm patterns" }],

  tags: ["Cinematic", "Afrobeat", "Gospel", "Lo-fi", "Electronic"]
},
{
  id: "lipsync",
  icon: "Mic",
  title: "AI Character Generator",
  subtitle: "Lip Sync Video",
  description: "Upload an image and audio to create talking character videos with realistic lip movement and facial animation.",
  color: "#EC4899",
  gradient: "linear-gradient(135deg, #EC4899, #F43F5E)",
  mode: "lip-sync",
  examples: [
  { title: "Talking Avatar", plays: "4.5K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7b9142a-1773074689373.png", alt: "Animated talking character with realistic lip sync" },
  { title: "Podcast Presenter", plays: "3.8K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_141a684b4-1773074692878.png", alt: "Professional podcast presenter with animated face" },
  { title: "Cartoon Speaker", plays: "7.2K", img: "https://img.rocket.new/generatedImages/rocket_gen_img_18465b644-1773074691793.png", alt: "Cartoon character speaking with expressive animations" }],

  tags: ["Realistic", "Cartoon", "Podcast", "Cinematic"]
}];


function ToolCard({ tool, onSelect }) {
  return (
    <div
      className="rounded-2xl overflow-hidden border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer group"
      style={{ background: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-md)" }}
      onClick={() => onSelect(tool?.mode)}>
      
      {/* Header */}
      <div className="p-5 pb-3" style={{ background: `${tool?.color}08` }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: tool?.gradient }}>
            <Icon name={tool?.icon} size={20} color="white" />
          </div>
          <div>
            <p className="text-xs font-semibold" style={{ color: tool?.color, fontFamily: "var(--font-caption)" }}>{tool?.subtitle}</p>
            <h3 className="text-sm font-bold" style={{ color: "var(--color-foreground)", fontFamily: "var(--font-heading)" }}>{tool?.title}</h3>
          </div>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
          {tool?.description}
        </p>
        <div className="flex flex-wrap gap-1 mt-3">
          {tool?.tags?.map((tag) =>
          <span key={tag} className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${tool?.color}15`, color: tool?.color, fontFamily: "var(--font-caption)" }}>
              {tag}
            </span>
          )}
        </div>
      </div>
      {/* Example creations */}
      <div className="p-3 pt-2">
        <p className="text-xs font-semibold mb-2" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>Example Creations</p>
        <div className="grid grid-cols-3 gap-1.5">
          {tool?.examples?.map((ex) =>
          <div key={ex?.title} className="relative rounded-lg overflow-hidden" style={{ aspectRatio: "1" }}>
              <Image src={ex?.img} alt={ex?.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" showSkeleton={true} />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)" }} />
              <div className="absolute bottom-1 left-1 right-1">
                <p className="text-white font-semibold leading-tight" style={{ fontFamily: "var(--font-caption)", fontSize: 8 }}>{ex?.title}</p>
                <p className="text-white/70" style={{ fontFamily: "var(--font-data)", fontSize: 7 }}>{ex?.plays} plays</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* CTA */}
      <div className="px-3 pb-3">
        <button
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-90"
          style={{ background: tool?.gradient, color: "white", fontFamily: "var(--font-heading)" }}>
          
          <Icon name="Wand2" size={13} color="white" />
          Try {tool?.subtitle}
        </button>
      </div>
    </div>);

}

export default function AIToolSection({ onToolSelect }) {
  return (
    <section className="py-10 md:py-14 lg:py-16">
      <div className="text-center mb-8 md:mb-10">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-4 text-xs font-semibold"
          style={{ background: "rgba(99,102,241,0.08)", borderColor: "var(--color-primary)", color: "var(--color-primary)" }}>
          
          <Icon name="Layers" size={13} color="var(--color-primary)" />
          5 Powerful AI Tools
        </div>
        <h2
          className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl mb-3"
          style={{ color: "var(--color-foreground)" }}>
          
          Everything You Can Create
        </h2>
        <p className="text-sm md:text-base max-w-xl mx-auto" style={{ color: "var(--color-muted-foreground)" }}>
          One platform. Five AI-powered creation tools. Unlimited possibilities.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
        {AI_TOOLS?.map((tool) =>
        <ToolCard key={tool?.id} tool={tool} onSelect={onToolSelect} />
        )}
      </div>
    </section>);

}